import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddTaskRoundedIcon from '@mui/icons-material/AddTaskRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import { API_BASE_URL, fetchJson } from './api';
import { buildActionRepository, isActionInCurrentWeek } from './actionRepository';
import ConfirmLiveProjectsDialog from './ConfirmLiveProjectsDialog';
import {
    AnalyticsInlineState,
    AnalyticsPageHero,
    AnalyticsPanelSection,
    analyticsBodyCellSx,
    analyticsHeadCellSx,
    analyticsTableSx
} from './analyticsSurface';

function getAuthHeaders(json = false) {
    const token = localStorage.getItem('monsurv_auth_token') || '';
    return {
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`
    };
}

function formatDate(value) {
    if (!value) return 'No due date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'No due date';
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

function statusPresentation(status) {
    const value = String(status || '').toLowerCase();
    if (value === 'completed') return { label: 'Completed', color: 'success' };
    if (value === 'overdue') return { label: 'Overdue', color: 'error' };
    if (value === 'due_today') return { label: 'Due today', color: 'warning' };
    if (value === 'due_soon') return { label: 'Due soon', color: 'warning' };
    if (value === 'on_track') return { label: 'On track', color: 'info' };
    if (value === 'paused') return { label: 'Paused', color: 'secondary' };
    return { label: 'Open', color: 'default' };
}

function actionMatchesFilter(action, filter) {
    if (filter === 'today') return action.status === 'due_today';
    if (filter === 'week') return !action.due_at ? action.status !== 'completed' : isActionInCurrentWeek(action);
    if (filter === 'priority') return Boolean(action.priority_flag) && action.status !== 'completed';
    if (filter === 'completed') return action.status === 'completed';
    return action.status !== 'completed';
}

const CLASSIFICATION_OPTIONS = [
    { value: 'project_specific', label: 'Project Specific' },
    { value: 'business_development', label: 'Business Development' },
    { value: 'internal_admin', label: 'Internal Admin' },
    { value: 'other', label: 'Other' }
];

export default function ActionsHub({ authUser }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [repository, setRepository] = useState({ myActions: [], teamActions: [], users: [], currentUserKey: '' });
    const [filter, setFilter] = useState('open');
    const [teamUserKey, setTeamUserKey] = useState('');
    const [search, setSearch] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ title: '', description: '', due_at: '', priority: false, classification: '', related_id: '', related_name: '', custom_note: '' });
    const [projects, setProjects] = useState([]);
    const [clients, setClients] = useState([]);
    const [workflow, setWorkflow] = useState({ open: false, type: '', action: null });
    const [reportUploadConfirmation, setReportUploadConfirmation] = useState({ open: false, action: null });
    const [targetUser, setTargetUser] = useState('');
    const [dismissalReason, setDismissalReason] = useState('');
    const [sendDelegationEmail, setSendDelegationEmail] = useState(false);
    const [delegationReason, setDelegationReason] = useState('');
    const [delegationWarning, setDelegationWarning] = useState('');
    const [saving, setSaving] = useState(false);
    const [sendingReport, setSendingReport] = useState(false);
    const [reportMessage, setReportMessage] = useState('');
    const [confirmLiveAction, setConfirmLiveAction] = useState(null);

    const loadActions = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const options = { method: 'GET', headers: getAuthHeaders() };
            const [sla, actionState, projectRows, clientRows] = await Promise.all([
                fetchJson(`${API_BASE_URL}/api/sla/tracking?days=45`, options),
                fetchJson(`${API_BASE_URL}/api/dashboard/priority-actions`, options),
                fetchJson(`${API_BASE_URL}/key_project_data`, options),
                fetchJson(`${API_BASE_URL}/funders`, options)
            ]);
            setRepository(buildActionRepository({ slaItems: sla?.items, actionState, authUser }));
            setProjects((Array.isArray(projectRows) ? projectRows : [])
                .map((project) => ({ reference: String(project?.reference || '').trim(), name: String(project?.project_name || project?.name || '').trim() }))
                .filter((project) => project.reference)
                .sort((left, right) => left.reference.localeCompare(right.reference, undefined, { numeric: true })));
            setClients((Array.isArray(clientRows) ? clientRows : [])
                .map((client) => ({ id: String(client?.id || '').trim(), name: String(client?.companyName || client?.company_name || '').trim() }))
                .filter((client) => client.id && client.name)
                .sort((left, right) => left.name.localeCompare(right.name)));
        } catch (loadError) {
            setError(loadError?.message || 'Unable to load actions.');
        } finally {
            setLoading(false);
        }
    }, [authUser]);

    useEffect(() => {
        loadActions();
    }, [loadActions]);

    const filteredMine = useMemo(() => {
        const query = search.trim().toLowerCase();
        return repository.myActions
            .filter((action) => actionMatchesFilter(action, filter))
            .filter((action) => !query || `${action.title} ${action.description} ${action.reference} ${action.detail_type} ${action.detail_text}`.toLowerCase().includes(query))
            .sort((left, right) => {
                if (left.priority_flag !== right.priority_flag) return left.priority_flag ? -1 : 1;
                if (!left.due_at) return 1;
                if (!right.due_at) return -1;
                return new Date(left.due_at).getTime() - new Date(right.due_at).getTime();
            });
    }, [repository.myActions, filter, search]);

    const teamActions = useMemo(() => repository.teamActions
        .filter((action) => action.status !== 'completed')
        .sort((left, right) => {
            if (!left.due_at) return 1;
            if (!right.due_at) return -1;
            return new Date(left.due_at).getTime() - new Date(right.due_at).getTime();
        }), [repository.teamActions]);

    const teamMembers = useMemo(() => {
        const members = new Map();
        repository.users.forEach((person) => {
            if (person?.key) members.set(person.key, { key: person.key, name: String(person.name || person.email || 'Team member').trim() });
        });
        teamActions.forEach((action) => {
            if (action?.owner_key && !members.has(action.owner_key)) {
                members.set(action.owner_key, { key: action.owner_key, name: String(action.owner_name || 'Team member').trim() });
            }
        });
        return Array.from(members.values()).sort((left, right) => left.name.localeCompare(right.name));
    }, [repository.users, teamActions]);

    useEffect(() => {
        if (!teamMembers.length) {
            if (teamUserKey) setTeamUserKey('');
            return;
        }
        if (!teamMembers.some((person) => person.key === teamUserKey)) setTeamUserKey(teamMembers[0].key);
    }, [teamMembers, teamUserKey]);

    const selectedTeamActions = useMemo(
        () => teamActions.filter((action) => action.owner_key === teamUserKey),
        [teamActions, teamUserKey]
    );

    const canCreateAction = useMemo(() => {
        if (!createForm.title.trim() || !createForm.classification) return false;
        if (createForm.classification === 'project_specific') return Boolean(createForm.related_id);
        if (createForm.classification === 'business_development') {
            return Boolean(createForm.related_id) && (createForm.related_id !== 'other' || Boolean(createForm.related_name.trim()));
        }
        if (createForm.classification === 'other') return Boolean(createForm.custom_note.trim());
        return true;
    }, [createForm]);

    async function createAction() {
        if (!createForm.title.trim() || saving) return;
        setSaving(true);
        setError('');
        try {
            await fetchJson(`${API_BASE_URL}/api/actions-hub`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: JSON.stringify({
                    title: createForm.title,
                    description: createForm.description,
                    due_at: createForm.due_at ? new Date(`${createForm.due_at}T17:00:00`).toISOString() : null,
                    priority: createForm.priority,
                    classification: createForm.classification,
                    related_id: createForm.related_id,
                    related_name: createForm.related_name,
                    custom_note: createForm.custom_note
                })
            });
            setCreateOpen(false);
            setCreateForm({ title: '', description: '', due_at: '', priority: false, classification: '', related_id: '', related_name: '', custom_note: '' });
            await loadActions();
        } catch (saveError) {
            setError(saveError?.message || 'Unable to create the action.');
        } finally {
            setSaving(false);
        }
    }

    async function sendActionReport() {
        if (sendingReport) return;
        setSendingReport(true);
        setReportMessage('');
        setError('');
        try {
            const openActions = repository.myActions.filter((action) => action.status !== 'completed');
            const result = await fetchJson(`${API_BASE_URL}/api/actions-hub/send-report`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: JSON.stringify({
                    actions: openActions.map((action) => ({
                        title: action.title,
                        description: action.description,
                        due_at: action.due_at,
                        status: action.status,
                        classification: action.classification,
                        related_name: action.related_name,
                        priority: action.priority_flag
                    }))
                })
            });
            setReportMessage(`Action report sent to ${result?.recipient || 'your email address'}.`);
        } catch (sendError) {
            setError(sendError?.message || 'Unable to send the Action Report.');
        } finally {
            setSendingReport(false);
        }
    }

    async function updateManualAction(action, updates) {
        setSaving(true);
        setError('');
        try {
            await fetchJson(`${API_BASE_URL}/api/actions-hub/${encodeURIComponent(action.action_id)}`, {
                method: 'PATCH',
                headers: getAuthHeaders(true),
                body: JSON.stringify(updates)
            });
            await loadActions();
        } catch (saveError) {
            setError(saveError?.message || 'Unable to update the action.');
        } finally {
            setSaving(false);
        }
    }

    function toggleActionCompletion(action) {
        if (action.direct_completion || action.source === 'manual') {
            updateManualAction(action, { completed: action.status !== 'completed' });
            return;
        }
        if (!action.workflow_completed) {
            setReportUploadConfirmation({ open: true, action });
            return;
        }
        applyWorkflow(action.workflow_completed ? 'reopen' : 'complete', action);
    }

    async function applyWorkflow(type, action, extra = {}) {
        if (!action || saving) return;
        setSaving(true);
        setError('');
        try {
            const result = await fetchJson(`${API_BASE_URL}/api/dashboard/priority-actions`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: JSON.stringify({
                    action: type,
                    reference: action.reference,
                    report_instance: action.report_instance,
                    ...extra
                })
            });
            setWorkflow({ open: false, type: '', action: null });
            setReportUploadConfirmation({ open: false, action: null });
            setTargetUser('');
            setDismissalReason('');
            setSendDelegationEmail(false);
            setDelegationReason('');
            if (result?.delegation_email?.warning) {
                setDelegationWarning(`Action delegated, but the email could not be sent: ${result.delegation_email.warning}`);
            }
            await loadActions();
        } catch (saveError) {
            setError(saveError?.message || 'Unable to update the action.');
        } finally {
            setSaving(false);
        }
    }

    function openWorkflow(type, action) {
        setTargetUser('');
        setDismissalReason('');
        setSendDelegationEmail(false);
        setDelegationReason('');
        setDelegationWarning('');
        setWorkflow({ open: true, type, action });
    }

    function closeWorkflow() {
        if (saving) return;
        setWorkflow({ open: false, type: '', action: null });
        setTargetUser('');
        setDismissalReason('');
        setSendDelegationEmail(false);
        setDelegationReason('');
    }

    function openReportUploadPortal(action) {
        const reference = String(action?.report_reference || action?.related_id || '').trim();
        if (!reference) return;
        const reportInstance = String(action?.target_report_instance || '').trim().toLowerCase();
        const params = new URLSearchParams({ portal: 'surveyor', reference });
        const interimMatch = reportInstance.match(/^interim:(\d+)$/);
        if (interimMatch) {
            params.set('page', 'interim');
            params.set('interim', interimMatch[1]);
        } else {
            params.set('page', 'initial');
        }
        window.location.assign(`${window.location.pathname}?${params.toString()}`);
    }

    function renderTable(actions, teamView = false, showOwner = teamView) {
        if (loading) return <AnalyticsInlineState kind="loading" message="Loading actions..." />;
        if (!actions.length) return <AnalyticsInlineState title={teamView ? 'No team actions' : 'No matching actions'} message={teamView ? 'No open actions are currently assigned to other team members.' : 'Adjust the filters or add a new action.'} />;

        return (
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={analyticsTableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...analyticsHeadCellSx, minWidth: { xs: 210, md: 280 } }}>Action</TableCell>
                            <TableCell sx={{ ...analyticsHeadCellSx, minWidth: { xs: 190, md: 220 } }}>Detail</TableCell>
                            <TableCell sx={analyticsHeadCellSx}>Source</TableCell>
                            {showOwner ? <TableCell sx={analyticsHeadCellSx}>Owner</TableCell> : null}
                            <TableCell sx={analyticsHeadCellSx}>Due</TableCell>
                            <TableCell sx={analyticsHeadCellSx}>Status</TableCell>
                            {!teamView ? <TableCell align="right" sx={analyticsHeadCellSx}>Controls</TableCell> : null}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {actions.map((action) => {
                            const status = statusPresentation(action.status);
                            const isConfirmLiveAction = action.action_type === 'confirm_live_projects';
                            return (
                                <TableRow
                                    key={action.key}
                                    hover
                                    onClick={isConfirmLiveAction ? () => setConfirmLiveAction(action) : undefined}
                                    sx={{ opacity: action.status === 'completed' ? 0.68 : 1, cursor: isConfirmLiveAction ? 'pointer' : 'default' }}
                                >
                                    <TableCell sx={analyticsBodyCellSx}>
                                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                                            {action.priority_flag ? <FlagRoundedIcon sx={{ color: '#dc2626', fontSize: 18, mt: 0.25 }} /> : null}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 800 }}>{action.title}</Typography>
                                                {action.description ? <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{action.description}</Typography> : null}
                                                {action.delegated_by ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>Delegated by {action.delegated_by}</Typography> : null}
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={analyticsBodyCellSx}>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{action.detail_type}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                            {action.detail_text}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={analyticsBodyCellSx}>
                                        <Chip size="small" variant="outlined" label={action.source === 'manual' ? 'Personal' : 'System'} />
                                    </TableCell>
                                    {showOwner ? <TableCell sx={analyticsBodyCellSx}>{action.owner_name || 'Unassigned'}</TableCell> : null}
                                    <TableCell sx={{ ...analyticsBodyCellSx, whiteSpace: 'nowrap' }}>{formatDate(action.due_at)}</TableCell>
                                    <TableCell sx={analyticsBodyCellSx}><Chip size="small" color={status.color} label={status.label} /></TableCell>
                                    {!teamView ? (
                                        <TableCell align="right" sx={{ ...analyticsBodyCellSx, whiteSpace: 'nowrap' }}>
                                            {isConfirmLiveAction ? (
                                                <Tooltip title="View projects">
                                                    <IconButton size="small" aria-label="View projects" onClick={() => setConfirmLiveAction(action)}>
                                                        <LaunchRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : action.action_type === 'book_site_visit' ? (
                                                <Tooltip title="Book in Calendar">
                                                    <IconButton size="small" aria-label="Book in Calendar" onClick={() => { window.location.href = '/?view=calendar'; }}>
                                                        <LaunchRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={action.source_completed ? 'Completed by report workflow' : (action.status === 'completed' ? 'Reopen action' : 'Mark complete')}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={action.source_completed ? 'Completed by report workflow' : (action.status === 'completed' ? 'Reopen action' : 'Mark complete')}
                                                            disabled={saving || action.source_completed}
                                                            onClick={() => toggleActionCompletion(action)}
                                                        >
                                                            {action.status === 'completed' ? <UndoRoundedIcon fontSize="small" /> : <CheckCircleOutlineRoundedIcon fontSize="small" />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {!isConfirmLiveAction && action.action_type === 'upload_report' ? (
                                                <Tooltip title="Open Information Portal">
                                                    <IconButton size="small" aria-label="Open Information Portal" onClick={() => openReportUploadPortal(action)}>
                                                        <LaunchRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                            {!isConfirmLiveAction ? <Tooltip title={action.priority_flag ? 'Remove priority' : 'Set priority'}>
                                                <span>
                                                    <IconButton size="small" disabled={saving} onClick={() => applyWorkflow(action.priority_flag ? 'remove_priority' : 'set_priority', action)}>
                                                        {action.priority_flag ? <FlagRoundedIcon fontSize="small" sx={{ color: '#dc2626' }} /> : <FlagOutlinedIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip> : null}
                                            {!isConfirmLiveAction ? <Tooltip title="Delegate action">
                                                <span><IconButton size="small" disabled={saving} onClick={() => openWorkflow('delegate', action)}><PersonAddAlt1RoundedIcon fontSize="small" /></IconButton></span>
                                            </Tooltip> : null}
                                            {!isConfirmLiveAction ? <Tooltip title="Dismiss action">
                                                <span><IconButton size="small" disabled={saving} onClick={() => openWorkflow('dismiss', action)}><RemoveCircleOutlineRoundedIcon fontSize="small" /></IconButton></span>
                                            </Tooltip> : null}
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
            <AnalyticsPageHero
                title="Actions Hub"
                description="Plan your daily and weekly workload, manage system tasks, and keep sight of team commitments."
                chips={[`${repository.myActions.filter((action) => action.status !== 'completed').length} open actions`, `${repository.myActions.filter((action) => action.priority_flag && action.status !== 'completed').length} priorities`]}
                actions={(
                    <>
                        <Button variant="outlined" startIcon={<EmailOutlinedIcon />} onClick={sendActionReport} disabled={sendingReport || loading}>
                            {sendingReport ? 'Sending...' : 'Send Action Report'}
                        </Button>
                        <Button variant="contained" startIcon={<AddTaskRoundedIcon />} onClick={() => setCreateOpen(true)}>New Action</Button>
                    </>
                )}
            />

            {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
            {delegationWarning ? <Alert severity="warning" onClose={() => setDelegationWarning('')}>{delegationWarning}</Alert> : null}
            {reportMessage ? <Alert severity="success" onClose={() => setReportMessage('')}>{reportMessage}</Alert> : null}

            <AnalyticsPanelSection
                title="My Actions"
                subtitle="Your system-generated and personal actions in one working list."
                bodySx={{ p: 0 }}
                right={(
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Search actions"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                            sx={{ width: { xs: 180, sm: 240 } }}
                        />
                        <Tooltip title="Refresh actions"><IconButton onClick={loadActions} disabled={loading}><RefreshRoundedIcon /></IconButton></Tooltip>
                    </Stack>
                )}
            >
                <Tabs value={filter} onChange={(_, value) => setFilter(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 1.5, borderBottom: `1px solid ${alpha('#0f172a', 0.06)}` }}>
                    <Tab value="open" label="Open" />
                    <Tab value="today" label="Today" />
                    <Tab value="week" label="This Week" />
                    <Tab value="priority" label="Priority" />
                    <Tab value="completed" label="Completed" />
                </Tabs>
                <Box sx={{ p: filteredMine.length || loading ? 0 : 2 }}>{renderTable(filteredMine)}</Box>
            </AnalyticsPanelSection>

            <AnalyticsPanelSection title="Team Actions" subtitle="Open commitments grouped by team member." bodySx={{ p: 0 }}>
                {teamMembers.length ? (
                    <Tabs
                        value={teamUserKey || false}
                        onChange={(_, value) => setTeamUserKey(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Team member actions"
                        sx={{ px: 1.5, borderBottom: `1px solid ${alpha('#0f172a', 0.06)}` }}
                    >
                        {teamMembers.map((person) => {
                            const actionCount = teamActions.filter((action) => action.owner_key === person.key).length;
                            return <Tab key={person.key} value={person.key} label={`${person.name} (${actionCount})`} />;
                        })}
                    </Tabs>
                ) : null}
                <Box sx={{ p: selectedTeamActions.length || loading ? 0 : 2 }}>
                    {renderTable(selectedTeamActions, true, false)}
                </Box>
            </AnalyticsPanelSection>

            <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Create Action</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            select
                            required
                            label="Action classification"
                            value={createForm.classification}
                            onChange={(event) => setCreateForm((current) => ({ ...current, classification: event.target.value, related_id: '', related_name: '', custom_note: '' }))}
                        >
                            {CLASSIFICATION_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                        </TextField>
                        {createForm.classification === 'project_specific' ? (
                            <TextField select required label="Related project" value={createForm.related_id} onChange={(event) => setCreateForm((current) => ({ ...current, related_id: event.target.value }))}>
                                {projects.map((project) => <MenuItem key={project.reference} value={project.reference}>{project.reference} - {project.name || 'Unnamed project'}</MenuItem>)}
                            </TextField>
                        ) : null}
                        {createForm.classification === 'business_development' ? (
                            <TextField select required label="Related client" value={createForm.related_id} onChange={(event) => setCreateForm((current) => ({ ...current, related_id: event.target.value, related_name: '' }))}>
                                {clients.map((client) => <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>)}
                                <MenuItem value="other">Other</MenuItem>
                            </TextField>
                        ) : null}
                        {createForm.classification === 'business_development' && createForm.related_id === 'other' ? (
                            <TextField required label="Client name" value={createForm.related_name} onChange={(event) => setCreateForm((current) => ({ ...current, related_name: event.target.value }))} inputProps={{ maxLength: 200 }} />
                        ) : null}
                        {createForm.classification === 'other' ? (
                            <TextField required label="Classification note" value={createForm.custom_note} onChange={(event) => setCreateForm((current) => ({ ...current, custom_note: event.target.value }))} inputProps={{ maxLength: 500 }} />
                        ) : null}
                        <TextField autoFocus required label="Action" value={createForm.title} onChange={(event) => setCreateForm((current) => ({ ...current, title: event.target.value }))} inputProps={{ maxLength: 160 }} />
                        <TextField multiline minRows={3} label="Details" value={createForm.description} onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))} inputProps={{ maxLength: 2000 }} />
                        <TextField type="date" label="Due date" value={createForm.due_at} onChange={(event) => setCreateForm((current) => ({ ...current, due_at: event.target.value }))} InputLabelProps={{ shrink: true }} />
                        <FormControlLabel control={<Checkbox checked={createForm.priority} onChange={(event) => setCreateForm((current) => ({ ...current, priority: event.target.checked }))} />} label="Add to My Priority Actions" />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={createAction} disabled={saving || !canCreateAction}>{saving ? 'Creating...' : 'Create Action'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={workflow.open} onClose={closeWorkflow} fullWidth maxWidth="sm">
                <DialogTitle>{workflow.type === 'delegate' ? 'Delegate Action' : 'Dismiss Action'}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{workflow.action?.title}</Typography>
                        {workflow.type === 'delegate' ? (
                            <Stack spacing={2}>
                                <TextField select fullWidth label="Delegate to" value={targetUser} onChange={(event) => setTargetUser(event.target.value)}>
                                    {repository.users.map((person) => <MenuItem key={person.key} value={person.key}>{person.name}{person.email ? ` (${person.email})` : ''}</MenuItem>)}
                                </TextField>
                                <FormControlLabel
                                    control={<Checkbox checked={sendDelegationEmail} onChange={(event) => setSendDelegationEmail(event.target.checked)} />}
                                    label="Send Email to User"
                                />
                                {sendDelegationEmail ? (
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        label="Reason for delegation"
                                        value={delegationReason}
                                        onChange={(event) => setDelegationReason(event.target.value)}
                                        inputProps={{ maxLength: 2000 }}
                                        required
                                    />
                                ) : null}
                            </Stack>
                        ) : (
                            <TextField autoFocus fullWidth multiline minRows={4} label="Reason for dismissal" value={dismissalReason} onChange={(event) => setDismissalReason(event.target.value)} inputProps={{ maxLength: 2000 }} />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeWorkflow} disabled={saving}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={saving || (workflow.type === 'delegate' ? (!targetUser || (sendDelegationEmail && !delegationReason.trim())) : !dismissalReason.trim())}
                        onClick={() => applyWorkflow(workflow.type, workflow.action, workflow.type === 'delegate'
                            ? { target_user_key: targetUser, send_email: sendDelegationEmail, delegation_reason: sendDelegationEmail ? delegationReason : undefined }
                            : { description: dismissalReason })}
                    >
                        {saving ? 'Saving...' : (workflow.type === 'delegate' ? 'Delegate' : 'Dismiss')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={reportUploadConfirmation.open}
                onClose={() => !saving && setReportUploadConfirmation({ open: false, action: null })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Report upload confirmation</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        <Typography sx={{ fontWeight: 800 }}>{reportUploadConfirmation.action?.title}</Typography>
                        <Typography>Has the completed report been uploaded to the Information Portal?</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Selecting No will complete this action and create a new System action to upload the PDF. That action will close automatically after a successful Information Portal upload, or it can be completed manually.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        disabled={saving}
                        onClick={() => applyWorkflow('complete', reportUploadConfirmation.action, { report_uploaded: false })}
                    >
                        No
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={() => applyWorkflow('complete', reportUploadConfirmation.action, { report_uploaded: true })}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmLiveProjectsDialog action={confirmLiveAction} onClose={() => setConfirmLiveAction(null)} />
        </Box>
    );
}